import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  FileCheck2,
  Loader2,
  LockKeyhole,
  RotateCw
} from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { PickupDriftInspectorSheet } from '@/components/reports/PickupDriftInspectorSheet';
import {
  formatPickupInvestorDisplayName,
  formatPickupInvestorRoute
} from '@/components/reports/pickupInvestorDisplay';
import {
  fetchPickupDriftInspectorData,
  reverifyPickupLoan,
  type PickupDriftInspectorData
} from '@/components/reports/pickupDriftInspectorService';
import type { PickupComparisonView } from '@/components/reports/pickupReconciliationPolicy';
import { pickupPointsToDollars } from '@/components/reports/pickupPricingMath';
import { pickupInvestorPricingContextFromOpportunity } from '@/components/reports/pickupInvestorPricing';
import { PICKUP_OPTIMAL_BLUE_LABEL } from '@/components/reports/pickupVerificationPresentation';
import { PickupPriceProvenance } from './PickupPriceProvenance';
import { PickupPipelineVerificationPanel } from './PickupPipelineVerificationPanel';
import {
  applyPickupOpportunityAction,
  fetchPickupOpportunityEvents,
  fetchPickupOpportunityPricingEvidence,
  getPickupLockConfirmationUrl,
  uploadPickupLockConfirmation,
  type PickupOpportunityPricingEvidence
} from '@/services/pickupOpportunityService';
import type {
  PickupOpportunityAction,
  PickupOpportunityEvent,
  PickupPipelineUser
} from '@/types/pickupOpportunity';
import {
  pickupAttemptStatusLabel,
  pickupLadderEconomics,
  pickupLadderOpenLabel,
  pickupLadderRungState,
  pickupMasterStatusLabel,
  pickupUserName,
  pickupVerificationRequestBlock,
  type PickupPipelineRow
} from './pickupPipelineModel';
import {
  dateTimeLocalToIso,
  formatPickupCurrency,
  formatPickupDate,
  formatPickupDateTime,
  formatPickupPoints,
  formatPickupPrice,
  toDateTimeLocal
} from './pickupPipelineFormatters';

interface PickupDispositionSheetProps {
  row: PickupPipelineRow | null;
  users: PickupPipelineUser[];
  open: boolean;
  canManageActions?: boolean;
  onUpdated: () => Promise<void>;
  onSelectNext?: () => void;
  previewData?: PickupDispositionPreviewData;
}

export interface PickupDispositionPreviewData {
  events: PickupOpportunityEvent[];
  pricingEvidence: PickupOpportunityPricingEvidence | null;
  comparison: PickupDriftInspectorData;
  savedMessage?: string;
}

const DECLINE_REASONS = [
  ['investor_declined', 'Investor declined'],
  ['pricing_unavailable', 'Pricing unavailable'],
  ['loan_ineligible', 'Loan ineligible'],
  ['terms_changed', 'Loan terms changed'],
  ['other', 'Other']
] as const;

const TRANSFER_REASONS = [
  ['guideline_ineligible', 'Guideline ineligible'],
  ['investor_wont_accept', "Investor won't accept"],
  ['product_unavailable', 'Product unavailable'],
  ['channel_mismatch', 'Channel mismatch'],
  ['amount_ltv_fico_restriction', 'Amount / LTV / FICO restriction'],
  ['closing_too_close', 'Closing too close'],
  ['lock_transfer_policy', 'Lock transfer policy'],
  ['fees_eliminated_pickup', 'Fees eliminated pickup'],
  ['pricing_no_longer_available', 'Pricing no longer available'],
  ['loan_terms_changed', 'Loan terms changed'],
  ['documentation_issue', 'Documentation issue'],
  ['other', 'Other']
] as const;

function userOptions(users: PickupPipelineUser[]) {
  return users.map((user) => ({ value: user.id, label: pickupUserName(user) }));
}

function evidenceValue(value: ReactNode, label: string) {
  return (
    <div className="rounded-lg border bg-muted/20 px-3 py-2">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function comparisonTarget(
  row: PickupPipelineRow,
  pricingEvidence: PickupOpportunityPricingEvidence | null = null
) {
  return {
    encompassLoanId: row.opportunity.encompass_loan_id,
    obLoanCacheId: row.opportunity.ob_loan_cache_id,
    obLoanId: row.opportunity.ob_loan_id,
    loanNumber: row.opportunity.los_loan_number,
    borrowerName: row.opportunity.borrower_name,
    pricing: pickupInvestorPricingContextFromOpportunity(
      row.opportunity,
      pricingEvidence,
      row.attempts
    )
  };
}

function dispositionActionLabel(status: string | undefined): string | null {
  switch (status) {
    case 'renegotiation_required':
      return 'Request repricing';
    case 'pending_investor_response':
      return 'Record answer';
    case 'checking_guidelines':
      return 'Complete guideline review';
    case 'guidelines_passed':
      return 'Start investor switch';
    case 'switching_investor':
      return 'Mark awaiting lock';
    case 'awaiting_lock_upload':
      return 'Complete transfer';
    default:
      return null;
  }
}

export function PickupDispositionSheet({
  row,
  users,
  open,
  canManageActions = true,
  onUpdated,
  onSelectNext,
  previewData
}: PickupDispositionSheetProps) {
  const [events, setEvents] = useState<PickupOpportunityEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [pricingEvidence, setPricingEvidence] = useState<PickupOpportunityPricingEvidence | null>(null);
  const [pricingEvidenceLoading, setPricingEvidenceLoading] = useState(false);
  const [pricingEvidenceError, setPricingEvidenceError] = useState<string | null>(null);
  const [comparisonData, setComparisonData] = useState<PickupDriftInspectorData | null>(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState<string | null>(null);
  const [comparisonView, setComparisonView] = useState<PickupComparisonView>('all');
  const [reverifying, setReverifying] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(previewData?.savedMessage || null);
  const [submitting, setSubmitting] = useState(false);
  const [ownerId, setOwnerId] = useState('');
  const [contactedAt, setContactedAt] = useState(toDateTimeLocal());
  const [notes, setNotes] = useState('');
  const [finalPrice, setFinalPrice] = useState('');
  const [approvalAt, setApprovalAt] = useState(toDateTimeLocal());
  const [declineReason, setDeclineReason] = useState('');
  const [declineComment, setDeclineComment] = useState('');
  const [reviewerId, setReviewerId] = useState('');
  const [guidelineStartedAt, setGuidelineStartedAt] = useState(toDateTimeLocal());
  const [guidelineNotes, setGuidelineNotes] = useState('');
  const [cannotReason, setCannotReason] = useState('');
  const [cannotComment, setCannotComment] = useState('');
  const [skipTargetId, setSkipTargetId] = useState('');
  const [skipReason, setSkipReason] = useState('');
  const [actualInvestor, setActualInvestor] = useState('');
  const [actualProduct, setActualProduct] = useState('');
  const [finalRate, setFinalRate] = useState('');
  const [finalLockPeriod, setFinalLockPeriod] = useState('');
  const [finalExpiration, setFinalExpiration] = useState(toDateTimeLocal());
  const [completionAt, setCompletionAt] = useState(toDateTimeLocal());
  const [transferCosts, setTransferCosts] = useState('0');
  const [confirmationFile, setConfirmationFile] = useState<File | null>(null);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [comparisonReturnFocus, setComparisonReturnFocus] = useState<HTMLButtonElement | null>(null);
  const dispositionRef = useRef<HTMLElement | null>(null);

  const activeAttempt = row?.activeAttempt ?? null;
  const options = useMemo(() => userOptions(users), [users]);
  const queuedAttempts = row?.attempts.filter((attempt) => attempt.status === 'queued') || [];
  const opportunityIdentity = row?.opportunity.id || '';
  const formIdentity = `${row?.opportunity.id || ''}:${activeAttempt?.id || ''}`;
  const selectedContextRef = useRef({ row, activeAttempt });
  selectedContextRef.current = { row, activeAttempt };
  const driftInspectorTarget = useMemo(
    () => row ? comparisonTarget(row, pricingEvidence) : null,
    [pricingEvidence, row]
  );
  const handleDriftInspectorDataLoaded = useCallback((nextData: PickupDriftInspectorData) => {
    setComparisonData(nextData);
    setComparisonView(nextData.defaultView);
  }, []);

  useEffect(() => {
    const selectedRow = selectedContextRef.current.row;
    const selectedAttempt = selectedContextRef.current.activeAttempt;
    setOwnerId(selectedRow?.opportunity.owner_id || '');
    setReviewerId('');
    setContactedAt(toDateTimeLocal());
    setGuidelineStartedAt(toDateTimeLocal());
    setApprovalAt(toDateTimeLocal());
    setCompletionAt(toDateTimeLocal());
    setFinalExpiration(toDateTimeLocal(selectedRow?.opportunity.latest_lock_expiration_at));
    setNotes('');
    setFinalPrice('');
    setDeclineReason('');
    setDeclineComment('');
    setGuidelineNotes('');
    setCannotReason('');
    setCannotComment('');
    setSkipTargetId('');
    setSkipReason('');
    setActualInvestor(selectedAttempt?.investor_name || '');
    setActualProduct(selectedAttempt?.product_name || '');
    setFinalRate(selectedAttempt?.latest_observed_rate?.toString() || selectedAttempt?.quoted_rate?.toString() || '');
    setFinalLockPeriod(selectedAttempt?.basis_lock_period?.toString() || '');
    setTransferCosts('0');
    setConfirmationFile(null);
    setComparisonOpen(false);
    setComparisonReturnFocus(null);
    setComparisonView('all');
  }, [formIdentity]);

  useEffect(() => {
    setSavedMessage(previewData?.savedMessage || null);
  }, [opportunityIdentity, previewData?.savedMessage]);

  useEffect(() => {
    let cancelled = false;
    const opportunityId = row?.opportunity.id;
    if (!open || !opportunityId) {
      setEvents([]);
      return undefined;
    }
    if (previewData) {
      setEvents(previewData.events);
      setEventsLoading(false);
      return undefined;
    }
    setEventsLoading(true);
    void fetchPickupOpportunityEvents(opportunityId)
      .then((nextEvents) => {
        if (!cancelled) {
          setEvents(nextEvents);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          toast.error(error instanceof Error ? error.message : 'Could not load the audit timeline.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setEventsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, previewData, row?.opportunity.id]);

  useEffect(() => {
    let cancelled = false;
    if (!open || !row) {
      setPricingEvidence(null);
      setPricingEvidenceError(null);
      setPricingEvidenceLoading(false);
      return () => {
        cancelled = true;
      };
    }
    if (previewData) {
      setPricingEvidence(previewData.pricingEvidence);
      setPricingEvidenceError(null);
      setPricingEvidenceLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setPricingEvidenceLoading(true);
    setPricingEvidenceError(null);
    void fetchPickupOpportunityPricingEvidence(row.opportunity.latest_result_id)
      .then((nextEvidence) => {
        if (!cancelled) {
          setPricingEvidence(nextEvidence);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setPricingEvidence(null);
          setPricingEvidenceError(error instanceof Error ? error.message : 'Could not load stored price evidence.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPricingEvidenceLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, previewData, row]);

  const loadComparison = useCallback(async () => {
    if (!row) {
      setComparisonData(null);
      setComparisonError(null);
      return;
    }
    if (previewData) {
      setComparisonData(previewData.comparison);
      setComparisonView(previewData.comparison.defaultView);
      setComparisonError(null);
      setComparisonLoading(false);
      return;
    }
    setComparisonLoading(true);
    setComparisonError(null);
    try {
      const nextData = await fetchPickupDriftInspectorData(comparisonTarget(row));
      setComparisonData(nextData);
      setComparisonView(nextData.defaultView);
    } catch (error) {
      setComparisonData(null);
      setComparisonError(error instanceof Error
        ? error.message
        : `Could not load the ${PICKUP_OPTIMAL_BLUE_LABEL} comparison.`);
    } finally {
      setComparisonLoading(false);
    }
  }, [previewData, row]);

  useEffect(() => {
    if (open && row) {
      void loadComparison();
    }
  }, [loadComparison, open, row]);

  if (!row) {
    return (
      <section className="flex h-full min-h-0 items-center justify-center bg-card p-8 text-center">
        <div>
          <h2 className="text-lg font-semibold">Select a Pick Up loan</h2>
          <p className="mt-1 text-sm text-muted-foreground">Choose a loan from the queue to review its pricing evidence, investor ladder, and required disposition.</p>
        </div>
      </section>
    );
  }

  const opportunity = row.opportunity;
  const isOpen = opportunity.status === 'open';
  const bestAltPrice = Number(opportunity.latest_best_alt_price) || null;
  const enteredFinalPrice = Number(finalPrice) || null;
  const leftOnTablePoints = bestAltPrice !== null && enteredFinalPrice !== null
    ? Math.max(0, bestAltPrice - enteredFinalPrice)
    : null;
  const leftOnTableDollars = leftOnTablePoints !== null && opportunity.loan_amount
    ? pickupPointsToDollars(leftOnTablePoints, opportunity.loan_amount)
    : null;

  const refreshAfterAction = async () => {
    await onUpdated();
    if (previewData) {
      setEvents(previewData.events);
      return;
    }
    const nextEvents = await fetchPickupOpportunityEvents(opportunity.id);
    setEvents(nextEvents);
  };

  const submitAction = async (
    action: PickupOpportunityAction,
    payload: Record<string, unknown>,
    file?: File | null
  ) => {
    if (!canManageActions) {
      toast.error('Pick Up Admin access is required to update dispositions.');
      return;
    }
    if (!activeAttempt) {
      return;
    }
    setSubmitting(true);
    try {
      if (file) {
        await uploadPickupLockConfirmation(opportunity.id, activeAttempt.id, file);
      }
      await applyPickupOpportunityAction({
        opportunityId: opportunity.id,
        attemptId: activeAttempt.id,
        action,
        expectedStatus: activeAttempt.status,
        payload
      });
      setSavedMessage(`Saved · ${pickupAttemptStatusLabel(action)}`);
      toast.success('Pick Up disposition updated.');
      await refreshAfterAction();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Pick Up disposition failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const openConfirmation = async (attemptId: string) => {
    try {
      const url = await getPickupLockConfirmationUrl(opportunity.id, attemptId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not open the lock confirmation.');
    }
  };

  const ownerNameById = new Map(users.map((user) => [user.id, pickupUserName(user)]));
  const statusLabel = isOpen
    ? pickupAttemptStatusLabel(activeAttempt?.status)
    : pickupMasterStatusLabel(opportunity.status);
  const ladderEconomics = pickupLadderEconomics(row);
  const verificationRequestBlock = pickupVerificationRequestBlock(row);
  const primaryActionLabel = dispositionActionLabel(activeAttempt?.status);
  const primaryActionBlocked = Boolean(
    verificationRequestBlock
    && ['renegotiation_required', 'checking_guidelines'].includes(activeAttempt?.status || '')
  );
  const investorRoute = formatPickupInvestorRoute(
    opportunity.original_investor,
    row.displayAttempt?.investor_name
  );

  const handleReverify = async () => {
    setReverifying(true);
    try {
      const result = await reverifyPickupLoan(opportunity.ob_loan_id);
      toast.success(result.message);
      await onUpdated();
      await loadComparison();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'OB re-verification failed.');
    } finally {
      setReverifying(false);
    }
  };

  const focusDisposition = () => {
    dispositionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <section
        data-pickup-pipeline-detail
        aria-label={`Pick Up detail for ${opportunity.los_loan_number || opportunity.ob_loan_id}`}
        className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-card"
      >
        <header className="shrink-0 border-b bg-muted/15 px-5 py-3.5 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <h2 className="truncate font-serif text-xl font-semibold leading-none">
                  {opportunity.borrower_name || 'Borrower unavailable'}
                </h2>
                <span className="text-[11px] font-semibold text-muted-foreground tabular-nums">
                  {opportunity.los_loan_number || opportunity.ob_loan_id}
                </span>
                <Badge variant="outline" className="h-6 whitespace-nowrap px-2 text-[10px]">
                  {statusLabel}
                </Badge>
              </div>
              <p className="mt-1.5 text-[11px] leading-none text-muted-foreground tabular-nums">
                {formatPickupCurrency(opportunity.loan_amount)} · {Number(opportunity.original_lock_rate).toFixed(3)}% · {opportunity.basis_lock_period}d basis · expires {formatPickupDate(opportunity.latest_lock_expiration_at)} · {investorRoute} · {row.ownerName} · Candidate {row.candidatePosition || '—'} of {row.candidateCount}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="xs"
                disabled={reverifying}
                onClick={() => void handleReverify()}
              >
                {reverifying
                  ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  : <RotateCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />}
                Re-verify
              </Button>
              {canManageActions && primaryActionLabel ? (
                <Button type="button" size="xs" disabled={primaryActionBlocked} onClick={focusDisposition}>
                  {primaryActionLabel}<ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              ) : null}
            </div>
          </div>
        </header>

        {savedMessage && (
          <div data-pickup-saved-strip className="shrink-0 border-b border-success/30 bg-success/10 px-5 py-2 text-[11px] font-semibold text-success-strong sm:px-6">
            <CheckCircle2 className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />
            {savedMessage}
          </div>
        )}

        {!canManageActions && (
          <Alert className="shrink-0 rounded-none border-x-0 border-t-0 border-warning/40 bg-warning/10">
            <LockKeyhole className="h-4 w-4" />
            <AlertTitle>Read-only Pick Up access</AlertTitle>
            <AlertDescription>
              Pick Up Admin access is required to update dispositions. Re-verification and supporting evidence remain available.
            </AlertDescription>
          </Alert>
        )}

        <div data-pickup-pipeline-detail-scroll className="min-h-0 flex-1 overflow-y-auto">
          <div className="border-b xl:grid xl:grid-cols-2">
            <div className="min-w-0 border-b px-5 py-4 sm:px-6 xl:border-b-0 xl:border-r">
              <PickupPriceProvenance
                opportunity={opportunity}
                evidence={pricingEvidence}
                loading={pricingEvidenceLoading}
                error={pricingEvidenceError}
              />
            </div>
            <div className="min-w-0 px-5 py-4 sm:px-6">
              <PickupPipelineVerificationPanel
                data={comparisonData}
                loading={comparisonLoading}
                error={comparisonError}
                view={comparisonView}
                onViewChange={setComparisonView}
                onOpenInspector={(element) => {
                  setComparisonReturnFocus(element);
                  setComparisonOpen(true);
                }}
              />
            </div>
          </div>

          {activeAttempt?.decayed_at && (
            <Alert className="m-4 rounded-none border-warning/50 bg-warning/10 sm:mx-6">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Active candidate price moved below threshold</AlertTitle>
              <AlertDescription>
                It was {formatPickupPoints(activeAttempt.decayed_from_pickup_points)} and is now {formatPickupPoints(activeAttempt.latest_observed_pickup_points)}. This warning does not close or advance the candidate.
              </AlertDescription>
            </Alert>
          )}

          <div className="px-5 py-5 sm:px-6">
            <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_336px]">
              <section aria-labelledby="pickup-investor-ladder" className="min-w-0">
                <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
                  <div className="contents">
                    <h3 id="pickup-investor-ladder" className="text-[9.5px] font-semibold uppercase tracking-[0.13em] text-muted-foreground">Investor ladder</h3>
                    {activeAttempt ? (
                      <p className="text-[11.5px] font-medium text-foreground/75">
                        Candidate {row.candidatePosition} of {row.candidateCount} open · {formatPickupCurrency(ladderEconomics.activePotentialDollars)} on the table
                        {ladderEconomics.nextPotentialDollars !== null
                          ? ` · ${formatPickupCurrency(ladderEconomics.nextPotentialDollars)} remains if ${formatPickupInvestorDisplayName(ladderEconomics.activeInvestor) || ladderEconomics.activeInvestor} declines`
                          : ' · no queued candidate remains'}
                      </p>
                    ) : (
                      <p className="text-[11.5px] font-medium text-foreground/75">{row.candidateCount} candidate{row.candidateCount === 1 ? '' : 's'} in the permanent record</p>
                    )}
                  </div>
                </div>

                <ol className="space-y-2" aria-label="Ranked investor candidates">
                  {row.attempts.map((attempt) => {
                    const rungState = pickupLadderRungState(attempt, opportunity.active_attempt_id);
                    const answered = rungState === 'answered';
                    const stale = rungState === 'stale';
                    const queued = rungState === 'queued';
                    const attemptPoints = attempt.latest_observed_pickup_points ?? attempt.quoted_pickup_points;
                    const attemptDollars = pickupPointsToDollars(attemptPoints, opportunity.loan_amount);
                    const outcomeEvent = events.find((event) =>
                      event.attempt_id === attempt.id
                      && (event.to_status === attempt.status || event.event_type === attempt.status)
                    );
                    const outcomeNote = attempt.outcome_comment
                      || attempt.stale_reason
                      || outcomeEvent?.note
                      || attempt.notes;
                    const outcomeAt = outcomeEvent?.created_at || attempt.completed_at || attempt.stale_at;
                    const outcomeActor = outcomeEvent?.actor_user_id
                      ? ownerNameById.get(outcomeEvent.actor_user_id) || 'User'
                      : outcomeEvent?.actor_kind || (attempt.owner_id
                        ? ownerNameById.get(attempt.owner_id) || 'Assigned user'
                        : 'System');
                    return (
                      <li
                        key={attempt.id}
                        data-pickup-rung-state={rungState}
                        className={`relative rounded-lg border px-3 py-2.5 ${
                          rungState === 'open'
                            ? 'border-warning bg-warning/5'
                            : queued
                              ? 'bg-muted/20 opacity-60'
                              : stale
                                ? 'border-muted bg-muted/30 opacity-70'
                                : 'border-success/25 bg-success/5'
                        }`}
                      >
                        <div className="flex items-start gap-2.5">
                          <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold tabular-nums ${
                            rungState === 'open'
                              ? 'border-warning bg-warning text-warning-foreground'
                              : answered
                                ? 'border-success/40 bg-success/10 text-success-strong'
                                : stale
                                  ? 'border-muted-foreground/30 bg-muted text-muted-foreground'
                                  : 'border-muted-foreground/30 bg-background text-muted-foreground'
                          }`}>
                            {attempt.rank}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="min-w-0 truncate text-[12px] font-semibold" title={formatPickupInvestorDisplayName(attempt.investor_name) || attempt.investor_name}>{formatPickupInvestorDisplayName(attempt.investor_name) || attempt.investor_name}</span>
                              <Badge variant="outline" className="h-5 px-1.5 text-[9px]">
                                {rungState === 'open'
                                  ? pickupLadderOpenLabel(attempt)
                                  : queued
                                    ? 'Locked'
                                    : pickupAttemptStatusLabel(attempt.status)}
                              </Badge>
                              <span className="ml-auto whitespace-nowrap font-mono text-[11.5px] font-semibold tabular-nums">
                                {formatPickupPrice(attempt.latest_observed_price ?? attempt.quoted_price)} · <span className="text-success">{formatPickupPoints(attemptPoints)} · {formatPickupCurrency(attemptDollars)}</span>
                              </span>
                            </div>

                            <div className="mt-1.5 text-[10.5px] leading-relaxed text-muted-foreground">
                              <span>{attempt.product_name || 'Product unavailable'} · {attempt.basis_lock_period}d basis · {attempt.mechanic === 'renegotiation' ? 'same investor' : 'investor transfer'}</span>
                              {queued && (
                                <span className="block">Unlocks after Candidate {activeAttempt?.rank || Math.max(1, attempt.rank - 1)} is resolved. Nothing is contacted automatically.</span>
                              )}
                              {(answered || stale) && (
                                <div className="mt-1 border-t pt-1.5">
                                  <span className="font-medium text-foreground">{pickupAttemptStatusLabel(attempt.status)}</span>
                                  <span> · {outcomeActor} · {formatPickupDateTime(outcomeAt)}</span>
                                  {outcomeNote && <p className="mt-1 text-muted-foreground">{outcomeNote}</p>}
                                </div>
                              )}
                            </div>

                            {canManageActions && rungState === 'open' && (
                              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                <Button type="button" size="sm" className="h-7 px-2.5 text-[10.5px]" disabled={primaryActionBlocked} onClick={focusDisposition}>
                                  {primaryActionLabel || 'Continue disposition'}
                                </Button>
                                {attempt.status === 'pending_investor_response' && (
                                  <Button type="button" variant="outline" size="sm" className="h-7 px-2.5 text-[10.5px]" onClick={focusDisposition}>
                                    Declined
                                  </Button>
                                )}
                                {attempt.mechanic === 'transfer' && (
                                  <Button type="button" variant="outline" size="sm" className="h-7 px-2.5 text-[10.5px]" onClick={focusDisposition}>
                                    Can't transfer
                                  </Button>
                                )}
                                {verificationRequestBlock && ['renegotiation_required', 'checking_guidelines'].includes(attempt.status) && (
                                  <span className="w-full text-[10px] font-medium text-warning-foreground">{verificationRequestBlock}</span>
                                )}
                              </div>
                            )}

                            {attempt.lock_confirmation_storage_path && (
                              <Button variant="ghost" size="sm" className="mt-2 h-7 px-2 text-xs" onClick={() => void openConfirmation(attempt.id)}>
                                <FileCheck2 className="mr-1 h-3.5 w-3.5" /> Open lock confirmation
                              </Button>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>

                {opportunity.status === 'not_captured_all_candidates_exhausted' && (
                  <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3.5 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-[11.5px] font-semibold text-destructive">All candidates exhausted</div>
                      <p className="mt-1 text-[10.5px] text-muted-foreground">Every candidate and reason remains in the ladder above.</p>
                    </div>
                    <span role="status" className="rounded-md border border-destructive/30 bg-background px-2.5 py-1.5 text-[10.5px] font-semibold text-destructive">
                      Closed as not captured
                    </span>
                  </div>
                )}
                {opportunity.status.startsWith('captured_') && (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-success/30 bg-success/5 p-3">
                    <div>
                      <div className="text-sm font-semibold text-success">Capture claimed</div>
                      <p className="mt-1 text-xs text-muted-foreground">Lock evidence and final economics are preserved below.</p>
                    </div>
                    {opportunity.terminal_attempt_id && row.attempts.find((attempt) =>
                      attempt.id === opportunity.terminal_attempt_id
                      && attempt.lock_confirmation_storage_path
                    ) && (
                      <Button variant="outline" size="sm" onClick={() => void openConfirmation(opportunity.terminal_attempt_id!)}>
                        <FileCheck2 className="mr-1 h-3.5 w-3.5" /> Open confirmation
                      </Button>
                    )}
                  </div>
                )}

                <div className="mt-3 flex items-start justify-end gap-2.5">
                  {canManageActions && (
                    <Textarea
                      aria-label="Note for the next disposition"
                      placeholder="Note for the answer you log next — desk confirmation, lock number, anything the auditor needs…"
                      className="min-h-11 flex-1 resize-y text-[11.5px]"
                      value={notes}
                      onChange={(event) => setNotes(event.target.value)}
                    />
                  )}
                  <Button type="button" variant="outline" className="h-10 whitespace-nowrap" disabled={!onSelectNext} onClick={onSelectNext}>
                    Next opportunity
                  </Button>
                </div>
              </section>

              <section aria-labelledby="pickup-audit-timeline" className="border-l bg-muted/15 px-5 py-1">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 id="pickup-audit-timeline" className="text-[9.5px] font-semibold uppercase tracking-[0.13em] text-muted-foreground">Attempt timeline · newest first</h3>
                  {eventsLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Loading audit timeline" />}
                </div>
                <ol className="space-y-0">
                  {events.map((event) => (
                    <li key={event.id} className="relative ml-1 border-l pb-3 pl-3 text-[11px] last:pb-0">
                      <span className="absolute -left-1 top-1 h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
                      <div className="font-semibold leading-tight">{pickupAttemptStatusLabel(event.event_type)}</div>
                      <div className="mt-0.5 text-[10.5px] leading-relaxed text-muted-foreground">
                        {event.actor_user_id ? ownerNameById.get(event.actor_user_id) || 'User' : event.actor_kind} · {formatPickupDateTime(event.created_at)}
                        {event.from_status || event.to_status ? ` · ${event.from_status || '—'} → ${event.to_status || '—'}` : ''}
                      </div>
                      {event.note && <p className="mt-1 leading-relaxed text-foreground/80">{event.note}</p>}
                    </li>
                  ))}
                  {!eventsLoading && events.length === 0 && (
                    <li className="py-6 text-center text-xs text-muted-foreground">No activity recorded.</li>
                  )}
                </ol>
              </section>
            </div>

            {canManageActions && isOpen && activeAttempt && (
              <section ref={dispositionRef} aria-labelledby="pickup-required-disposition" className="mt-6 space-y-4">
                <div>
                  <h3 id="pickup-required-disposition" className="text-sm font-semibold">Required disposition</h3>
                  <p className="text-xs text-muted-foreground">There is no dismiss or delete. Complete this candidate or advance with an audited reason.</p>
                </div>

                {verificationRequestBlock
                  && ['renegotiation_required', 'checking_guidelines'].includes(activeAttempt.status) && (
                  <Alert className="border-warning/40 bg-warning/10">
                    <LockKeyhole className="h-4 w-4" />
                    <AlertTitle>Request unavailable</AlertTitle>
                    <AlertDescription>{verificationRequestBlock}</AlertDescription>
                  </Alert>
                )}

                <div className="rounded-xl border p-4">
                  <h4 className="text-sm font-medium">Owner</h4>
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    <Select value={ownerId} onValueChange={setOwnerId}>
                      <SelectTrigger className="sm:max-w-sm" aria-label="Opportunity owner"><SelectValue placeholder="Select owner" /></SelectTrigger>
                      <SelectContent>{options.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      disabled={!ownerId || submitting}
                      onClick={() => void submitAction('assign_owner', { ownerId })}
                    >Save owner</Button>
                  </div>
                </div>

                {activeAttempt.status === 'renegotiation_required' && (
                  <form
                    className="space-y-3 rounded-xl border p-4"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void submitAction('start_renegotiation', {
                        ownerId,
                        contactedAt: dateTimeLocalToIso(contactedAt),
                        notes
                      });
                    }}
                  >
                    <div>
                      <h4 className="text-sm font-medium">Start renegotiation</h4>
                      <p className="text-xs text-muted-foreground">Use the alternate ladder above as negotiating evidence.</p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1"><Label htmlFor="pickup-contacted-at">Contacted date/time</Label><Input id="pickup-contacted-at" type="datetime-local" required value={contactedAt} onChange={(event) => setContactedAt(event.target.value)} /></div>
                      <div className="space-y-1"><Label htmlFor="pickup-reneg-owner">Owner</Label><Select value={ownerId} onValueChange={setOwnerId}><SelectTrigger id="pickup-reneg-owner"><SelectValue placeholder="Select owner" /></SelectTrigger><SelectContent>{options.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent></Select></div>
                    </div>
                    <div className="space-y-1"><Label htmlFor="pickup-reneg-notes">Notes (optional)</Label><Textarea id="pickup-reneg-notes" value={notes} onChange={(event) => setNotes(event.target.value)} /></div>
                    <Button type="submit" disabled={!ownerId || submitting || Boolean(verificationRequestBlock)}>{submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Request repricing</Button>
                  </form>
                )}

                {activeAttempt.status === 'pending_investor_response' && (
                  <div className="grid gap-4 lg:grid-cols-2">
                    <form
                      className="space-y-3 rounded-xl border p-4"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void submitAction('approve_renegotiation', {
                          finalPrice: Number(finalPrice),
                          approvalAt: dateTimeLocalToIso(approvalAt),
                          notes
                        }, confirmationFile);
                      }}
                    >
                      <h4 className="text-sm font-medium">Approved</h4>
                      <div className="space-y-1"><Label htmlFor="pickup-final-reneg-price">Final renegotiated price</Label><Input id="pickup-final-reneg-price" type="number" min={opportunity.original_lock_price + 0.001} step="0.001" required value={finalPrice} onChange={(event) => setFinalPrice(event.target.value)} /></div>
                      <div className="space-y-1"><Label htmlFor="pickup-approval-at">Approval date/time</Label><Input id="pickup-approval-at" type="datetime-local" required value={approvalAt} onChange={(event) => setApprovalAt(event.target.value)} /></div>
                      <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-xs">
                        Best alt {formatPickupPrice(bestAltPrice)} · leaving {formatPickupPoints(leftOnTablePoints)} / {formatPickupCurrency(leftOnTableDollars)} on the table.
                      </div>
                      <div className="space-y-1"><Label htmlFor="pickup-reneg-proof">Confirmation (optional, PDF/JPEG/PNG)</Label><Input id="pickup-reneg-proof" type="file" accept="application/pdf,image/jpeg,image/png" onChange={(event) => setConfirmationFile(event.target.files?.[0] || null)} /></div>
                      <div className="space-y-1"><Label htmlFor="pickup-approval-notes">Notes (optional)</Label><Textarea id="pickup-approval-notes" value={notes} onChange={(event) => setNotes(event.target.value)} /></div>
                      <Button type="submit" disabled={!finalPrice || submitting}>Record Approved Capture</Button>
                    </form>

                    <form
                      className="space-y-3 rounded-xl border p-4"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void submitAction('decline_renegotiation', {
                          reason: declineReason,
                          comment: declineComment
                        });
                      }}
                    >
                      <h4 className="text-sm font-medium">Declined</h4>
                      <div className="space-y-1"><Label>Reason</Label><Select value={declineReason} onValueChange={setDeclineReason}><SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger><SelectContent>{DECLINE_REASONS.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent></Select></div>
                      <div className="space-y-1"><Label htmlFor="pickup-decline-comment">Required comment</Label><Textarea id="pickup-decline-comment" required value={declineComment} onChange={(event) => setDeclineComment(event.target.value)} /></div>
                      <Button type="submit" variant="outline" disabled={!declineReason || !declineComment.trim() || submitting}>Record Decline and Advance</Button>
                    </form>
                  </div>
                )}

                {activeAttempt.status === 'checking_guidelines' && (
                  <form
                    className="space-y-3 rounded-xl border p-4"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void submitAction('pass_guidelines', {
                        reviewerId,
                        startedAt: dateTimeLocalToIso(guidelineStartedAt),
                        notes: guidelineNotes
                      });
                    }}
                  >
                    <h4 className="text-sm font-medium">Guideline review passed</h4>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1"><Label>Reviewer</Label><Select value={reviewerId} onValueChange={setReviewerId}><SelectTrigger><SelectValue placeholder="Select reviewer" /></SelectTrigger><SelectContent>{options.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent></Select></div>
                      <div className="space-y-1"><Label htmlFor="pickup-guideline-started">Review started</Label><Input id="pickup-guideline-started" type="datetime-local" required value={guidelineStartedAt} onChange={(event) => setGuidelineStartedAt(event.target.value)} /></div>
                    </div>
                    <div className="space-y-1"><Label htmlFor="pickup-guideline-notes">Review notes</Label><Textarea id="pickup-guideline-notes" required value={guidelineNotes} onChange={(event) => setGuidelineNotes(event.target.value)} /></div>
                    <Button type="submit" disabled={!reviewerId || !guidelineNotes.trim() || submitting || Boolean(verificationRequestBlock)}>Guidelines Passed</Button>
                  </form>
                )}

                {activeAttempt.status === 'guidelines_passed' && (
                  <form
                    className="space-y-3 rounded-xl border p-4"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void submitAction('start_investor_switch', { notes });
                    }}
                  >
                    <h4 className="text-sm font-medium">Start investor switch</h4>
                    <div className="space-y-1"><Label htmlFor="pickup-switch-notes">Notes (optional)</Label><Textarea id="pickup-switch-notes" value={notes} onChange={(event) => setNotes(event.target.value)} /></div>
                    <Button type="submit" disabled={submitting}>Switching Investor</Button>
                  </form>
                )}

                {activeAttempt.status === 'switching_investor' && (
                  <form
                    className="space-y-3 rounded-xl border p-4"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void submitAction('await_lock_upload', { notes });
                    }}
                  >
                    <h4 className="text-sm font-medium">Ready for new lock</h4>
                    <div className="space-y-1"><Label htmlFor="pickup-await-notes">Notes (optional)</Label><Textarea id="pickup-await-notes" value={notes} onChange={(event) => setNotes(event.target.value)} /></div>
                    <Button type="submit" disabled={submitting}>Awaiting Lock Upload</Button>
                  </form>
                )}

                {activeAttempt.status === 'awaiting_lock_upload' && (
                  <form
                    className="space-y-3 rounded-xl border p-4"
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (!activeAttempt.lock_confirmation_storage_path && !confirmationFile) {
                        toast.error('Upload the new lock confirmation.');
                        return;
                      }
                      void submitAction('complete_transfer', {
                        actualInvestor,
                        actualProduct,
                        finalRate: Number(finalRate),
                        finalPrice: Number(finalPrice),
                        lockPeriod: Number(finalLockPeriod),
                        lockExpirationAt: dateTimeLocalToIso(finalExpiration),
                        completionAt: dateTimeLocalToIso(completionAt),
                        transferCosts: Number(transferCosts || 0),
                        notes
                      }, confirmationFile);
                    }}
                  >
                    <h4 className="text-sm font-medium">Complete successful lock flip</h4>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      <div className="space-y-1"><Label htmlFor="pickup-actual-investor">Actual investor</Label><Input id="pickup-actual-investor" required value={actualInvestor} onChange={(event) => setActualInvestor(event.target.value)} /></div>
                      <div className="space-y-1"><Label htmlFor="pickup-actual-product">Actual product</Label><Input id="pickup-actual-product" required value={actualProduct} onChange={(event) => setActualProduct(event.target.value)} /></div>
                      <div className="space-y-1"><Label htmlFor="pickup-transfer-rate">Final rate</Label><Input id="pickup-transfer-rate" type="number" min="0.001" step="0.001" required value={finalRate} onChange={(event) => setFinalRate(event.target.value)} /></div>
                      <div className="space-y-1"><Label htmlFor="pickup-transfer-price">Final price</Label><Input id="pickup-transfer-price" type="number" min={opportunity.original_lock_price + 0.001} step="0.001" required value={finalPrice} onChange={(event) => setFinalPrice(event.target.value)} /></div>
                      <div className="space-y-1"><Label htmlFor="pickup-lock-period">Lock period</Label><Input id="pickup-lock-period" type="number" min="1" step="1" required value={finalLockPeriod} onChange={(event) => setFinalLockPeriod(event.target.value)} /></div>
                      <div className="space-y-1"><Label htmlFor="pickup-transfer-costs">Transfer costs</Label><Input id="pickup-transfer-costs" type="number" min="0" step="0.01" value={transferCosts} onChange={(event) => setTransferCosts(event.target.value)} /></div>
                      <div className="space-y-1"><Label htmlFor="pickup-final-expiration">Lock expiration</Label><Input id="pickup-final-expiration" type="datetime-local" required value={finalExpiration} onChange={(event) => setFinalExpiration(event.target.value)} /></div>
                      <div className="space-y-1"><Label htmlFor="pickup-completion-at">Completion date/time</Label><Input id="pickup-completion-at" type="datetime-local" required value={completionAt} onChange={(event) => setCompletionAt(event.target.value)} /></div>
                      <div className="space-y-1"><Label htmlFor="pickup-lock-proof">New lock confirmation</Label><Input id="pickup-lock-proof" type="file" required={!activeAttempt.lock_confirmation_storage_path} accept="application/pdf,image/jpeg,image/png" onChange={(event) => setConfirmationFile(event.target.files?.[0] || null)} /></div>
                    </div>
                    <div className="space-y-1"><Label htmlFor="pickup-transfer-notes">Notes (optional)</Label><Textarea id="pickup-transfer-notes" value={notes} onChange={(event) => setNotes(event.target.value)} /></div>
                    <Button type="submit" disabled={submitting}>Record Transferred Capture</Button>
                  </form>
                )}

                {activeAttempt.mechanic === 'transfer' && ['checking_guidelines', 'guidelines_passed', 'switching_investor', 'awaiting_lock_upload'].includes(activeAttempt.status) && (
                  <form
                    className="space-y-3 rounded-xl border border-destructive/30 p-4"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void submitAction('cannot_transfer', { reason: cannotReason, comment: cannotComment });
                    }}
                  >
                    <h4 className="text-sm font-medium">Cannot transfer</h4>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1"><Label>Reason</Label><Select value={cannotReason} onValueChange={setCannotReason}><SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger><SelectContent>{TRANSFER_REASONS.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent></Select></div>
                      <div className="space-y-1"><Label htmlFor="pickup-cannot-comment">Required comment</Label><Textarea id="pickup-cannot-comment" required value={cannotComment} onChange={(event) => setCannotComment(event.target.value)} /></div>
                    </div>
                    <Button type="submit" variant="outline" disabled={!cannotReason || !cannotComment.trim() || submitting}>Record Cannot Transfer and Advance</Button>
                  </form>
                )}

                {queuedAttempts.length > 0 && (
                  <form
                    className="space-y-3 rounded-xl border p-4"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void submitAction('skip_candidate', { targetAttemptId: skipTargetId, reason: skipReason });
                    }}
                  >
                    <h4 className="text-sm font-medium">Skip to another candidate</h4>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1"><Label>Target candidate</Label><Select value={skipTargetId} onValueChange={setSkipTargetId}><SelectTrigger><SelectValue placeholder="Select queued investor" /></SelectTrigger><SelectContent>{queuedAttempts.map((attempt) => <SelectItem key={attempt.id} value={attempt.id}>#{attempt.rank} · {formatPickupInvestorDisplayName(attempt.investor_name) || attempt.investor_name} · {formatPickupPrice(attempt.latest_observed_price ?? attempt.quoted_price)}</SelectItem>)}</SelectContent></Select></div>
                      <div className="space-y-1"><Label htmlFor="pickup-skip-reason">Required audited reason</Label><Textarea id="pickup-skip-reason" required value={skipReason} onChange={(event) => setSkipReason(event.target.value)} /></div>
                    </div>
                    <Button type="submit" variant="outline" disabled={!skipTargetId || !skipReason.trim() || submitting}>Skip and Activate Selected Candidate</Button>
                  </form>
                )}
              </section>
            )}

            {!isOpen && (
              <section aria-labelledby="pickup-capture-ledger" className="rounded-xl border p-4">
                <h3 id="pickup-capture-ledger" className="text-sm font-semibold">Outcome ledger</h3>
                <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  {evidenceValue(formatPickupPoints(opportunity.captured_points), 'Captured points')}
                  {evidenceValue(formatPickupCurrency(opportunity.captured_dollars), 'Gross captured')}
                  {evidenceValue(formatPickupCurrency(opportunity.transfer_costs), 'Transfer costs')}
                  {evidenceValue(formatPickupCurrency(opportunity.net_captured_dollars), 'Net captured')}
                </div>
                <div className="mt-3 text-xs text-muted-foreground">
                  Claimed {formatPickupDateTime(opportunity.captured_claimed_at)} · {opportunity.verified_at ? `Verified ${formatPickupDateTime(opportunity.verified_at)}` : 'Not yet verified'}
                </div>
              </section>
            )}

          </div>
        </div>
      </section>
      <PickupDriftInspectorSheet
        target={driftInspectorTarget}
        open={comparisonOpen}
        onOpenChange={setComparisonOpen}
        returnFocusElement={comparisonReturnFocus}
        onDataLoaded={handleDriftInspectorDataLoaded}
      />
    </>
  );
}
